import api from '../services/api';

/**
 * MIME-Typ → Dateiendung. Nur das, was wir selbst ausliefern (Bilder, Flyer, GPX) — eine
 * vollständige Tabelle wäre eine Pflegestelle für Typen, die hier nie ankommen.
 */
const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
  'application/gpx+xml': 'gpx',
  'application/xml': 'gpx',
  'text/xml': 'gpx',
};

/**
 * Reicht dem Browser ein Blob als Download.
 *
 * Die Object-URL wird erst nach dem Klick freigegeben, sonst ist die Adresse tot, bevor der
 * Browser sie liest.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/**
 * Holt eine Datei über die API und reicht sie als Download weiter.
 *
 * Nötig aus demselben Grund wie [loadAuthedImage]: ein nacktes `<a href download>` schickt keinen
 * Authorization-Header. Dateien an einem Entwurf, an einem Vorschlag oder im Postfach stehen aber
 * hinter einer Rechteprüfung — mit einem Link wäre der Download je nach Status still leer.
 *
 * `name` ist der Dateiname **ohne** Endung; die kommt aus dem Content-Type der Antwort. Der
 * Server bestimmt den Typ aus den Magic Bytes, er weiß es also besser als der Aufrufer — und der
 * kennt ihn vor dem Abruf ohnehin nicht.
 */
export async function downloadFromApi(path: string, name: string): Promise<void> {
  const response = await api.get(path, { responseType: 'blob' });
  const blob = response.data as Blob;
  const extension = EXTENSIONS[(blob.type || '').split(';')[0].trim().toLowerCase()] ?? 'bin';
  downloadBlob(blob, `${name}.${extension}`);
}

/**
 * Dateinamen-tauglicher Teil aus einem Titel: ASCII, Kleinbuchstaben, Bindestriche.
 *
 * Umlaute werden ausgeschrieben statt entfernt („Grün" → `gruen`, nicht `grn`), und die Länge ist
 * gekappt — Event-Titel gehen bis weit über hundert Zeichen, und manche Dateisysteme nicht.
 */
export function fileSlug(text: string, fallback = 'datei'): string {
  const slug = text.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
    .replace(/-$/, '');
  return slug || fallback;
}
